import React, { useState } from 'react';
import styled from 'styled-components';
import VersionSelector from '../components/common/VersionSelector';
import PageContainer from '../view/page-container';
import {
  termsServiceVersions,
  getLatestVersion,
} from '../data/termsServiceVersions';

const MainContent = styled.div`
  padding-top: 80px;
`;

const ContentWrapper = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 40px ${({ theme }) => theme.spacing.lg};
`;

const Title = styled.h1`
  font-size: ${({ theme }) => theme.typography.fontSize['4xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: 32px;
  text-align: center;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.typography.fontSize['3xl']};
  }
`;

const SubTitle = styled.h2`
  font-size: 20px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin: 40px 0 20px 0;
`;

const Article = styled.div`
  margin-bottom: 32px;
`;

const ArticleTitle = styled.h3`
  color: #000;

  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 158%; /* 25.28px */
  letter-spacing: -0.4px;
`;

const ArticleTextComponent = styled.p`
  color: #000;

  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 158%;
  letter-spacing: -0.4px;
`;

const NumberedArticleText = ({
  children,
  number,
}: {
  children: React.ReactNode;
  number?: number;
}) => {
  return (
    <div style={{ display: 'flex', flexDirection: 'row', gap: '4px' }}>
      {number && <ArticleTextComponent>{`${number}.`}</ArticleTextComponent>}
      <ArticleTextComponent>{children}</ArticleTextComponent>
    </div>
  );
};

const AlphabeticArticleText = ({
  children,
  letter,
}: {
  children: React.ReactNode;
  letter?: string;
}) => {
  return (
    <div style={{ display: 'flex', flexDirection: 'row', gap: '4px' }}>
      {letter && <ArticleTextComponent>{`${letter}.`}</ArticleTextComponent>}
      <ArticleTextComponent>{children}</ArticleTextComponent>
    </div>
  );
};

const ArticleText = ({ children }: { children: React.ReactNode }) => {
  if (typeof children === 'string') {
    const normalizedText = children.trimStart();
    const numberedMatch = normalizedText.match(/^(\d+)\.\s*([\s\S]*)$/);
    const alphabeticMatch = normalizedText.match(/^([a-zA-Z])\.\s*([\s\S]*)$/);

    if (numberedMatch) {
      return (
        <NumberedArticleText number={Number(numberedMatch[1])}>
          {numberedMatch[2].trimStart()}
        </NumberedArticleText>
      );
    }

    if (alphabeticMatch) {
      return (
        <AlphabeticArticleText letter={alphabeticMatch[1]}>
          {alphabeticMatch[2].trimStart()}
        </AlphabeticArticleText>
      );
    }
  }

  return <ArticleTextComponent>{children}</ArticleTextComponent>;
};

const ArticleBlockquoteText = styled.p`
  margin: 0;
  padding-left: 24px;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  margin: 40px 0;
`;

const TermsService: React.FC = () => {
  const [selectedVersion, setSelectedVersion] = useState(
    getLatestVersion().version,
  );

  const currentVersion =
    termsServiceVersions.find((v) => v.version === selectedVersion) ||
    getLatestVersion();

  const handleVersionChange = (version: string) => {
    setSelectedVersion(version);
  };

  return (
    <PageContainer>
      <MainContent>
        <ContentWrapper>
          <Title>{currentVersion.title}</Title>
          <VersionSelector
            versions={termsServiceVersions.map((v) => ({
              version: v.version,
              effectiveDate: v.version,
              title: v.title,
              isLatest: v.isLatest,
            }))}
            onVersionChange={handleVersionChange}
          />
          <Divider />
          <SubTitle>제1장 총칙</SubTitle>
          <Article>
            <ArticleTitle>제1조 (목적)</ArticleTitle>
            <ArticleText>
              이 약관은 빅브라운 주식회사(이하 “회사”)가 운영하는 팬 경험 기반
              오프라인 체험 플랫폼 ‘라라팡(LALAFAN)’(이하 “서비스”)의 이용과
              관련하여 회사와 회원 간의 권리·의무 및 책임사항을 규정함을
              목적으로 합니다.
            </ArticleText>
          </Article>
          <Article>
            <ArticleTitle>제2조 (정의)</ArticleTitle>
            <ArticleText>
              이 약관에서 사용하는 용어의 정의는 다음과 같습니다.
            </ArticleText>
            <ArticleText>
              1. “서비스”란 회사가 운영하는 정원 기반 참여 방식의 오프라인 팬
              경험 중개 플랫폼을 의미합니다.
            </ArticleText>
            <ArticleText>
              2. “회원”이란 본 약관에 동의하고 서비스 이용계약을 체결한 자를
              말합니다.
            </ArticleText>
            <ArticleText>
              3. “호스트”란 서비스 내에서 팬밋을 개설·운영하는 주체로서, 회사와
              직접 계약을 체결한 개인 또는 법인 사업자, 또는 비사업자
              인플루언서의 팬밋 운영을 위하여 회사와 별도의 계약을 체결한 제휴
              법인을 의미합니다.
            </ArticleText>
            <ArticleText>
              4. “제휴법인”이란 비사업자 인플루언서의 팬밋 운영을 위하여 회사와
              계약을 체결하고 팬밋의 주최자 및 정산 주체가 되는 법인을 말하며,
              현재 “(주)아보카도자이언트”가 이에 해당합니다. 회사는 운영상
              필요에 따라 제휴 법인을 추가·변경할 수 있으며, 변경 시 서비스 내
              공지합니다.
            </ArticleText>
            <ArticleText>
              5. “비사업자 인플루언서”란 개인사업자 또는 법인으로 등록되어 있지
              아니한 회원으로서, 팬밋 운영을 위하여 제휴 법인과 용역계약을
              체결하는 자를 말합니다.
            </ArticleText>
            <ArticleText>
              6. “사업자 인플루언서”란 개인사업자 또는 법인으로 등록된
              회원으로서, 팬밋 운영과 관련된 활동을 수행하는 자를 말합니다.
            </ArticleText>
            <ArticleText>
              7. “팬밋”이란 호스트가 기획·진행하는 팬미팅, 체험, 클래스 등
              오프라인 팬 경험 활동을 의미합니다.
            </ArticleText>
            <ArticleText>
              8. “참여자”란 팬밋에 참여 신청을 하고, 모집성공 시 결제를 완료한
              회원을 말합니다.
            </ArticleText>
            <ArticleText>
              9. “모집기간”이란 회사가 서비스 화면(팬밋 상세/신청 화면 등)에
              표시한 참여 접수 기간을 의미합니다.
            </ArticleText>
            <ArticleText>
              10. “참여”란 팬밋의 정원이 충족될 경우 결제가 이루어지는 조건부
              참여 신청을 의미합니다.
            </ArticleText>
            <ArticleText>
              11. “모집성공”이란 호스트가 설정한 참여 정원(또는 회사가 사전에
              고지한 확정 조건)이 모집기간 내 충족되어 팬밋의 진행이 확정된
              상태를 의미합니다.
            </ArticleText>
            <ArticleText>
              12. “중개서비스”란 회사가 결제, 환불, 고객지원 및 정산에 관한
              관리·운영 기능을 제공하는 통신판매중개 서비스를 의미합니다.
            </ArticleText>
          </Article>
          <Article>
            <ArticleTitle>제3조 (약관의 효력 및 변경)</ArticleTitle>
            <ArticleText>
              1. 본 약관은 서비스 화면에 게시함으로써 효력이 발생합니다.
            </ArticleText>
            <ArticleText>
              2. 회사는 관련 법령을 위반하지 않는 범위에서 약관을 변경할 수
              있습니다.
            </ArticleText>
            <ArticleText>
              3. 회원에게 불리한 변경의 경우 30일 전, 그 외 변경은 7일 전
              공지합니다.
            </ArticleText>
          </Article>
          <Article>
            <ArticleTitle>제4조 (약관 외 준칙)</ArticleTitle>
            <ArticleText>
              1. 본 약관에서 정하지 않은 사항은 전자상거래법, 약관규제법 등 관계
              법령 및 상관례에 따릅니다.
            </ArticleText>
            <ArticleText>
              2. 개인정보의 수집·이용·보관·파기에 관한 사항은 별도의 개인정보
              처리방침에 따릅니다.
            </ArticleText>
          </Article>

          <Divider />
          <SubTitle>제2장 회원가입 및 관리</SubTitle>
          <Article>
            <ArticleTitle>제1조 (회원가입)</ArticleTitle>
            <ArticleText>
              1. 회원가입은 약관 동의 및 정보 입력으로 이루어집니다.
            </ArticleText>
            <ArticleText>2. 만 14세 미만은 가입할 수 없습니다.</ArticleText>
          </Article>
          <Article>
            <ArticleTitle>제2조 (회원의 의무)</ArticleTitle>
            <ArticleText>회원은 다음 행위를 해서는 안 됩니다.</ArticleText>
            <ArticleText>· 허위 정보 기재</ArticleText>
            <ArticleText>· 타인 명의 결제수단 사용</ArticleText>
            <ArticleText>· 직거래 유도</ArticleText>
            <ArticleText>· 서비스 운영 방해 행위</ArticleText>
          </Article>
          <Article>
            <ArticleTitle>제3조 (회원자격 제한 및 탈퇴)</ArticleTitle>
            <ArticleText>1. 회원은 언제든지 탈퇴할 수 있습니다.</ArticleText>
            <ArticleText>
              2. 진행 중인 팬밋, 정산, 분쟁이 있는 경우 탈퇴가 제한될 수
              있습니다.
            </ArticleText>
          </Article>

          <Divider />
          <SubTitle>제3장 서비스 이용</SubTitle>
          <Article>
            <ArticleTitle>제1조 (서비스 제공 범위)</ArticleTitle>
            <ArticleText>회사는 다음 서비스를 제공합니다.</ArticleText>
            <ArticleText>· 팬밋 등록 및 심사</ArticleText>
            <ArticleText>· 참여 관리</ArticleText>
            <ArticleText>· 결제·환불·정산</ArticleText>
            <ArticleText>· 고객지원</ArticleText>
          </Article>
          <Article>
            <ArticleTitle>제2조 (서비스 제한)</ArticleTitle>
            <ArticleText>
              회사는 시스템 점검, 불가항력 사유로 서비스 제공을 제한할 수
              있습니다.
            </ArticleText>
          </Article>

          <Divider />
          <SubTitle>제4장 결제 및 모집성공</SubTitle>
          <Article>
            <ArticleTitle>제1조 (참여 및 모집성공)</ArticleTitle>
            <ArticleText>
              1. 참여자는 팬밋에 참여 신청을 할 수 있으며, 이 시점에는 결제가
              이루어지지 않습니다.
            </ArticleText>
            <ArticleText>
              2. 모집성공은 참여 인원이 호스트가 설정한 참여 정원(또는 회사가
              사전에 고지한 확정 조건)에 모집기간 내 충족한 시점에 자동으로
              이루어집니다. 확정 조건 및 모집기간은 팬밋 상세/신청 화면 등
              서비스 내 표시된 내용에 따릅니다.
            </ArticleText>
            <ArticleBlockquoteText>
              <ArticleText>
                a. 다만, 비정상적 참여, 중복 신청, 악의적 이용 등이 확인되는
                경우 회사는 해당 참여를 모집 인원 산정에서 제외할 수 있으며,
                모집성공 여부는 회사 시스템 기준에 따라 판단됩니다.
              </ArticleText>
            </ArticleBlockquoteText>
          </Article>
          <Article>
            <ArticleTitle>제2조 (결제 시점 및 실패 처리)</ArticleTitle>
            <ArticleText>
              1. 모집성공 시 회사는 등록된 결제수단으로 1회 자동결제를
              시도합니다.
            </ArticleText>
            <ArticleText>
              2. 결제 실패 시 참여자에게 안내가 이루어지며, 참여자는 24시간 내
              결제수단 변경 후 직접 결제를 완료해야 합니다.
            </ArticleText>
            <ArticleText>
              3. 24시간 내 결제가 이루어지지 않을 경우 해당 참여자의 참여는 자동
              취소됩니다.
            </ArticleText>
            <ArticleText>
              4. 결제 실패 이후의 결제는 참여자가 서비스 내 결제 화면을 통해
              직접 결제하며, 회사는 추가 자동결제를 수행하지 않습니다.
            </ArticleText>
            <ArticleText>
              5. 참여자가 기한 내 결제를 완료하지 아니한 경우, 해당 참여자의
              팬밋 참여는 성립되지 않은 것으로 봅니다.
            </ArticleText>
            <ArticleText>
              6. 본 조의 기한(24시간)은 회사 시스템에 기록된 결제 실패 안내 발송
              시각(한국표준시)을 기준으로 산정합니다.
            </ArticleText>
          </Article>
          <Article>
            <ArticleTitle>제3조 (결제 주체 및 구조)</ArticleTitle>
            <ArticleText>
              1. 모든 결제는 회사 명의로 이루어지며, 회사는 결제 처리 및 관리
              기능을 제공합니다.
            </ArticleText>
            <ArticleText>
              2. 팬밋의 주최자 및 실질적인 서비스 제공 책임자는 호스트이며,
              회사는 통신판매중개자로서의 지위만을 가집니다.
            </ArticleText>
            <ArticleText>
              3. 비사업자 인플루언서가 참여하는 팬밋의 경우, 해당 팬밋의 주최자
              및 정산 대상자는 제휴 법인입니다. 이 경우 참여자에게는 결제 전
              화면에서 주최자(제휴 법인)가 명확히 표시됩니다.
            </ArticleText>
          </Article>

          <Divider />
          <SubTitle>제5장 취소 및 환불</SubTitle>
          <Article>
            <ArticleTitle>제1조 (모집성공 이전)</ArticleTitle>
            <ArticleText>
              모집성공 이전에는 결제가 발생하지 않으며, 참여자는 자유롭게 참여를
              철회할 수 있습니다. 모집성공 이전의 철회는 결제 취소(환불)가 아닌
              신청 철회이며, 결제는 발생하지 않습니다.
            </ArticleText>
          </Article>
          <Article>
            <ArticleTitle>제2조 (모집실패)</ArticleTitle>
            <ArticleText>
              모집성공이 이루어지지 않은 팬밋은 자동 취소되며, 결제는 발생하지
              않습니다. 단, 회사의 정책에 따라 호스트에게 최소 이용료가 사전
              청구·결제된 경우, 모집실패로 팬밋이 자동 취소될 때 회사는 해당
              최소 이용료를 환급합니다.
            </ArticleText>
          </Article>
          <Article>
            <ArticleTitle>제3조 (모집성공 이후 환불 제한)</ArticleTitle>
            <ArticleText>
              1. 모집성공 이후에는 참여자의 단순 변심에 따른 취소 및 환불이
              제한되며, 이는 「전자상거래 등에서의 소비자보호에 관한 법률 시행령
              제17조 제2항」에서 정한 ‘일정이 정해진 체험형 서비스’에 해당하는
              경우로서, 결제 전 해당 사실이 명확히 고지된 경우에 한하여
              적용됩니다.
            </ArticleText>
            <ArticleText>
              2. 호스트 귀책, 불가항력, 법령상 예외 사유가 있는 경우에는 관계
              법령 및 회사의 환불정책에 따라 환불됩니다.
            </ArticleText>
            <ArticleText>
              3. 회사는 환불 기준, 환불 처리 기간, 불가항력 등 예외 기준을 결제
              전 화면 및 결제 화면에서 명확히 고지합니다.
            </ArticleText>
            <ArticleText>
              4. 회사의 환불정책은 관계 법령 및 본 약관에 반하지 않는 범위에서
              적용되며, 변경 시에는 제1장 제3조에 따른 공지 절차를 따릅니다.
            </ArticleText>
            <ArticleText>
              5. 환불 처리는 회사가 결제수단 및 PG/카드사 정책에 따라
              진행합니다.
            </ArticleText>
            <ArticleText>
              6. 환불 처리 기간은 결제수단 및 금융기관/카드사 정책에 따라 달라질
              수 있으며, 회사는 서비스 화면 또는 고객지원을 통해 처리 현황을
              안내합니다.
            </ArticleText>
          </Article>

          <Divider />
          <SubTitle>제6장 호스트 및 제휴 법인 용역계약</SubTitle>
          <Article>
            <ArticleTitle>제1조 (호스트 및 회사의 법적 지위)</ArticleTitle>
            <ArticleText>
              1. 호스트는 팬밋의 기획, 운영 및 현장 체험 제공에 대한 독립적인
              책임 주체입니다.
            </ArticleText>
            <ArticleText>
              2. 회사는 통신판매중개자로서 결제, 환불, 정산 시스템 및 고객지원
              기능을 제공합니다.
            </ArticleText>
            <ArticleText>
              3. 비사업자 인플루언서가 참여하는 팬밋의 경우, 제휴 법인이 팬밋의
              유일한 주최자이자 정산 대상자가 됩니다.
            </ArticleText>
          </Article>
          <Article>
            <ArticleTitle>
              제2조 (비사업자 인플루언서와 제휴 법인 간 용역계약의 성립)
            </ArticleTitle>
            <ArticleText>
              1. 비사업자 인플루언서 회원은 본 약관에 동의함으로써, 제휴 법인과
              팬밋 운영을 위한 기본 용역계약을 체결하는 것에 명시적으로
              동의합니다.
            </ArticleText>
            <ArticleText>
              2. 상기 용역계약은 근로계약이 아닌 독립된 용역계약이며, 비사업자
              인플루언서는 제휴 법인의 지휘, 감독을 받는 근로자가 아님을
              확인합니다.
            </ArticleText>
            <ArticleText>
              3. 비사업자 인플루언서에 대한 용역 보수의 지급, 지급 기준, 지급
              시기, 세무 처리 및 관련 신고 의무는 전적으로 제휴 법인의 책임으로
              합니다.
            </ArticleText>
            <ArticleText>
              4. 회사는 제휴 법인과 비사업자 인플루언서 간의 용역계약 내용, 보수
              산정 방식 및 지급 과정에 관여하지 않으며, 이에 대한 어떠한 책임도
              부담하지 않습니다.
            </ArticleText>
            <ArticleText>
              5. 비사업자 인플루언서와 제휴 법인 간 기본 용역계약의 주요 내용은
              본 약관에 포함된 관련 조항 및 서비스 화면에 표시된 개별 팬밋의
              조건에 따라 정해집니다.
            </ArticleText>
          </Article>
          <Article>
            <ArticleTitle>제3조 (제휴 법인에 대한 권한 위임)</ArticleTitle>
            <ArticleText>
              회사는 비사업자 인플루언서의 팬밋 운영을 위하여, 제휴 법인에게
              비사업자 인플루언서와의 용역계약 체결, 관리 및 정산에 관한 권한을
              위임합니다.
            </ArticleText>
          </Article>
          <Article>
            <ArticleTitle>제4조 (호스트의 운영 책임)</ArticleTitle>
            <ArticleText>
              1. 호스트는 팬밋의 기획, 진행, 운영, 안전관리 전반에 대한 책임을
              부담합니다.
            </ArticleText>
            <ArticleText>
              2. 팬밋 진행에 필요한 장소 확보, 법적 인허가, 신고, 보험 가입 및
              안전관리 의무는 전적으로 호스트의 책임으로 합니다.
            </ArticleText>
            <ArticleText>
              3. 팬밋 진행 중 발생하는 모든 사고, 분쟁, 손해에 대한 1차적 책임은
              호스트에게 있으며, 회사는 통신판매중개자로서 법령상 부담하는
              범위의 책임을 제외하고는 이에 대한 책임을 부담하지 않습니다.
            </ArticleText>
          </Article>
          <Article>
            <ArticleTitle>제5조 (안전관리 및 보험 의무)</ArticleTitle>
            <ArticleText>
              1. 호스트는 팬밋 진행 과정에서 발생할 수 있는 안전사고 및 위험
              요소를 사전에 파악하고 이를 방지하기 위한 인력·시설·운영 대책을
              마련하여야 합니다.
            </ArticleText>
            <ArticleText>
              2. 호스트는 팬밋 참여자에게 예상 가능한 위험 요소를 사전에
              고지하여 참여 여부를 판단할 수 있도록 해야 합니다.
            </ArticleText>
            <ArticleText>
              3. 법령상 보험 가입 의무가 있는 경우, 호스트는 반드시 보험에
              가입하여야 하며 그 증빙을 회사에 제출하여야 합니다.
            </ArticleText>
            <ArticleText>
              4. 보험 가입 의무가 없는 경우라도 보험에 가입한 경우에는 그 증빙을
              회사에 제출할 수 있습니다.
            </ArticleText>
          </Article>
          <Article>
            <ArticleTitle>제6조 (정산 기준 및 방식)</ArticleTitle>
            <ArticleText>
              1. 회사는 팬밋이 정상적으로 완료된 경우에 한하여 회사와 직접
              계약한 호스트에게 정산을 진행합니다.
            </ArticleText>
            <ArticleBlockquoteText>
              <ArticleText>
                본 조에서 “정상적으로 진행 완료”라 함은, 팬밋이 사전에 고지된
                일정과 내용에 따라 실제로 진행되고, 허위·미진행 또는 중대한
                하자가 확인되지 아니한 상태를 의미합니다.
              </ArticleText>
            </ArticleBlockquoteText>
            <ArticleText>
              2. 비사업자 인플루언서가 참여한 팬밋의 경우, 회사는 제휴 법인에게
              팬밋 대금을 일괄 정산합니다.
            </ArticleText>
            <ArticleText>
              3. 이 경우 제휴 법인은 관계 법령에 따라 비사업자 인플루언서에게
              지급되는 용역대금에 대하여 소득세 및 지방소득세를 포함한
              원천징수세액 3.3%를 공제한 후 잔액을 정산금으로 지급합니다.
            </ArticleText>
            <ArticleText>
              4. 원천징수세액 3.3%(소득세 및 지방소득세 포함)는 플랫폼 수수료
              공제 후 잔액을 기준으로 산정됩니다.
            </ArticleText>
            <ArticleText>
              5. 제휴 법인이 내부적으로 수행하는 비사업자 인플루언서에 대한
              용역대금 분배 및 지급에 대하여 회사는 관여하지 않으며 책임을
              부담하지 않습니다.
            </ArticleText>
            <ArticleText>
              6. 정산금은 총 결제금액에서 플랫폼 수수료(20%)를 공제한 금액으로
              산정됩니다.
            </ArticleText>
            <ArticleText>
              7. 플랫폼 수수료 총액이 50,000원 미만일 경우, 최소 이용료
              50,000원이 적용됩니다.
            </ArticleText>
            <ArticleText>
              8. 정산은 팬밋 종료일 기준 영업일 10일 이내에 이루어집니다. 다만,
              팬밋 진행 여부에 대한 합리적 확인이 필요하거나 분쟁·민원·조사 등이
              진행 중인 경우, 또는 호스트의 정산 정보/증빙이 미비한 경우 회사는
              정산을 보류할 수 있습니다. 이 경우 회사는 보류 사유 및 필요한
              조치를 호스트에게 안내합니다. 회사는 보류 사유가 해소되는 경우
              지체 없이 정산 절차를 재개합니다.
            </ArticleText>
            <ArticleText>
              9. 정산금은 반드시 호스트 본인 명의의 계좌로만 지급되며, 타인 명의
              계좌로의 지급은 허용되지 않습니다.
            </ArticleText>
            <ArticleText>
              10. 사업자 인플루언서는 회사로부터 정산금을 지급받은 경우, 관계
              법령에 따라 해당 정산금에 대한 전자세금계산서를 회사에 발행하여야
              합니다.
            </ArticleText>
            <ArticleText>
              11. 사업자 인플루언서는 정산금 지급일을 기준으로 익월 10일 이내에
              전자세금계산서를 발행하여야 하며, 해당 기간 내에 발행하지 못한
              경우에는 실제 발행일의 당일 날짜로 전자세금계산서를 발행하여야
              합니다.
            </ArticleText>
            <ArticleText>
              12. 전자세금계산서의 지연 발행, 미발행 또는 기재 오류 등으로
              인하여 발생하는 가산세 및 기타 세무상 불이익은 전적으로 사업자
              인플루언서가 부담하며, 이와 관련하여 회사는 어떠한 책임도 부담하지
              않습니다.
            </ArticleText>
          </Article>
          <Article>
            <ArticleTitle>
              제7조 (정산 정보 제공 및 세무 처리 동의)
            </ArticleTitle>
            <ArticleText>
              1. 회사와 직접 계약한 호스트는 정산 및 세무 처리를 위하여 필요한
              정보를 회사에 제공하여야 합니다.
            </ArticleText>
            <ArticleBlockquoteText>
              <ArticleText>· 성명(실명)</ArticleText>
              <ArticleText>· 주민등록번호 또는 사업자등록번호</ArticleText>
              <ArticleText>· 주소</ArticleText>
              <ArticleText>· 휴대전화번호</ArticleText>
              <ArticleText>· 은행명, 예금주, 계좌번호</ArticleText>
            </ArticleBlockquoteText>
            <ArticleText>
              2. 회사는 관련 법령 준수 및 정산·세무 처리에 필요한 경우, 신분확인
              서류, 계좌 확인 서류, 정산 관련 증빙 등 추가 서류 또는 정보의
              제출을 요청할 수 있습니다.
            </ArticleText>
            <ArticleText>
              3. 호스트는 회사가 관련 법령에 따라 사업소득 신고, 원천징수,
              세금계산서 발행, 지급명세서 제출 등 세무 업무를 수행하는 것에
              동의합니다. 다만, 비사업자 인플루언서에 대한 용역대금 지급 및 그에
              대한 세무 처리·신고 의무는 제휴 법인에 있습니다.
            </ArticleText>
            <ArticleText>
              4. 호스트는 타인의 명의 또는 계좌를 이용한 정산을 회사에 요구하지
              않을 것을 확약합니다.
            </ArticleText>
            <ArticleText>
              5. 호스트가 정산을 위하여 제공하여야 하는 정보가 누락되었거나,
              사실과 다르거나, 최신 정보로 갱신되지 아니하여 정산 처리가
              지연되는 경우, 해당 지연에 대한 책임은 전적으로 호스트에게
              있습니다.
            </ArticleText>
            <ArticleText>
              6. 회사는 호스트가 제공한 정보의 정확성 및 진위 여부를 실질적으로
              보증하거나 검증할 의무를 부담하지 않으며, 호스트는 본인이 제공한
              정보로 인하여 발생하는 모든 법적·행정적 책임을 부담합니다.
            </ArticleText>
            <ArticleText>
              7. 제휴 법인이 비사업자 인플루언서에게 지급하는 용역대금에 대한
              세무 처리 및 신고 의무는 제휴 법인에게 있습니다.
            </ArticleText>
          </Article>
          <Article>
            <ArticleTitle>제8조 (면책 및 구상권)</ArticleTitle>
            <ArticleText>
              1. 회사는 통신판매중개자로서 법령상 부담하는 책임의 범위를
              제외하고, 팬밋의 실제 진행, 품질, 안전 및 현장 운영에 관한 책임을
              부담하지 않습니다.
            </ArticleText>
            <ArticleText>
              2. 팬밋과 관련하여 제3자가 회사에 손해배상 또는 법적 책임을
              청구하는 경우, 호스트는 회사를 면책시켜야 합니다.
            </ArticleText>
            <ArticleText>
              3. 회사가 이용자 또는 제3자에게 손해를 배상한 경우, 회사는 해당
              금액에 대해 호스트에게 구상권을 행사할 수 있으며, 이는 정산금에서
              공제할 수 있습니다. 이 경우 회사는 공제 사유 및 산정 내역을
              호스트에게 사전에 통지합니다.
            </ArticleText>
            <ArticleText>
              4. 호스트는 공제 통지를 받은 날로부터 7일 이내에 이의 제기를 할 수
              있으며, 회사는 합리적인 범위에서 사실관계 확인 후 처리 결과를
              안내합니다. 다만, 법령·판결·조정·수사/조사기관 요청 등으로 즉시
              공제가 불가피한 경우에는 예외로 할 수 있습니다.
            </ArticleText>
          </Article>
          <Article>
            <ArticleTitle>제9조 (직접 거래 금지)</ArticleTitle>
            <ArticleText>
              1. 호스트는 서비스 외부에서 참여자와 직접 결제, 정산, 환불 또는
              거래를 진행할 수 없습니다.
            </ArticleText>
            <ArticleText>
              2. 이를 위반하여 발생한 모든 분쟁 및 손해에 대해 회사는 책임을
              지지 않습니다.
            </ArticleText>
          </Article>

          <Divider />
          <SubTitle>제7장 게시물 및 지식재산권</SubTitle>
          <Article>
            <ArticleTitle>제1조 (게시물 관리)</ArticleTitle>
            <ArticleText>
              1. 회원 게시물의 저작권은 회원에게 귀속됩니다. 회사는 서비스의
              운영, 노출, 품질 개선, 홍보 및 마케팅을 위해 필요한 범위 내에서
              해당 게시물을 비독점적으로 이용할 수 있습니다.
            </ArticleText>
            <ArticleBlockquoteText>
              <ArticleText>
                a. 회사는 법령 또는 회원의 별도 동의가 필요한 범위를 초과하여
                게시물을 이용하지 않습니다.
              </ArticleText>
              <ArticleText>
                b. 회사는 게시물의 본질을 변경하지 않는 범위에서 노출 형식에
                맞춘 크기 조정, 배열 변경 등 필요한 편집을 할 수 있습니다.
              </ArticleText>
            </ArticleBlockquoteText>
            <ArticleText>
              2. 회사는 관계 법령 또는 본 약관·운영정책에 위반되는 게시물에
              대하여 사전 통지 없이 삭제, 비공개, 접근 제한 등의 조치를 할 수
              있습니다.
            </ArticleText>
          </Article>
          <Article>
            <ArticleTitle>제2조 (콘텐츠 제한)</ArticleTitle>
            <ArticleText>
              1. 호스트는 팬밋의 기획·운영 및 서비스 제공과 관련하여, 사회
              통념상 부적절하거나 법령에 위반되는 콘텐츠를 제공하거나
              노출하여서는 안 됩니다.
            </ArticleText>
            <ArticleText>
              2. 다음 각 호의 콘텐츠는 명시적으로 금지됩니다.
            </ArticleText>
            <ArticleBlockquoteText>
              <ArticleText>
                a. 음주 또는 흡연을 조장하거나 미화하는 내용(미성년자에게
                음주·흡연을 권유하거나 노출하는 행위를 포함)
              </ArticleText>
              <ArticleText>
                b. 과도한 신체 노출, 성적 행위의 직접적·간접적 표현, 성적
                수치심을 유발하는 선정적 콘텐츠
              </ArticleText>
              <ArticleText>
                c. 폭력 행위, 위협, 혐오 표현, 신체적·정신적 학대를 조장하거나
                정당화하는 콘텐츠
              </ArticleText>
              <ArticleText>
                d. 도박, 사행성 행위, 불법 복권, 불법 베팅 등 사행심을 조장하는
                콘텐츠
              </ArticleText>
              <ArticleText>
                e. 마약, 향정신성 의약품, 불법 약물의 사용·유통·구매를
                조장하거나 이를 체험 요소로 포함하는 콘텐츠
              </ArticleText>
              <ArticleText>
                f. 범죄 행위 또는 위법 행위를 조장·모방·미화하거나 관련 정보를
                제공하는 콘텐츠
              </ArticleText>
              <ArticleText>
                g. 기타 「청소년 보호법」, 「형법」, 「정보통신망 이용촉진 및
                정보보호 등에 관한 법률」 등 관계 법령을 위반하거나 위반 우려가
                있는 콘텐츠
              </ArticleText>
            </ArticleBlockquoteText>
            <ArticleText>
              3. 회사는 제2항에 해당하는 콘텐츠가 확인될 경우, 사전 통보 없이
              해당 팬밋의 수정 요청, 노출 제한, 등록 거부, 삭제, 운영 중단 등의
              조치를 취할 수 있습니다.
            </ArticleText>
            <ArticleText>
              4. 호스트는 본 조를 위반하여 발생한 모든 민·형사상 책임을
              부담하며, 이로 인해 회사에 손해가 발생한 경우 회사를 면책하고 그
              손해를 배상하여야 합니다.
            </ArticleText>
          </Article>

          <Divider />
          <SubTitle>제8장 손해배상 및 면책</SubTitle>
          <Article>
            <ArticleTitle>제1조 (손해배상)</ArticleTitle>
            <ArticleText>
              회원의 귀책으로 회사에 손해가 발생한 경우 배상 책임이 있습니다.
            </ArticleText>
          </Article>
          <Article>
            <ArticleTitle>제2조 (회사의 책임 범위 및 면책)</ArticleTitle>
            <ArticleText>
              1. 회사는 통신판매중개자로서 팬밋과 관련된 결제 처리, 환불,
              고객지원 및 분쟁의 합리적 해결을 위한 조정 지원을 제공합니다.
            </ArticleText>
            <ArticleText>
              2. 회사의 조정 지원은 사실관계 확인 및 당사자 간 원활한 합의를
              돕기 위한 범위에 한하며, 법적 분쟁의 당사자가 되거나 특정 결과를
              보장하지는 않습니다.
            </ArticleText>
            <ArticleText>
              3. 다만, 팬밋의 기획, 실제 진행, 현장 운영, 안전관리 및 체험
              제공의 내용과 품질에 관한 책임은 이를 직접 수행하는 호스트에게
              있습니다.
            </ArticleText>
            <ArticleText>
              4. 회사는 호스트가 제공하는 팬밋의 내용에 대해 사전에 심사 및 관리
              절차를 운영하나, 이는 서비스의 안전성과 신뢰성을 높이기 위한
              조치일 뿐, 팬밋의 실제 제공 결과에 대한 보증을 의미하지는
              않습니다.
            </ArticleText>
            <ArticleText>
              5. 천재지변, 공공기관의 제한 조치, 불가항력적인 사유로 인하여
              발생한 손해에 대하여 회사는 관련 법령이 허용하는 범위 내에서
              책임을 제한할 수 있습니다.
            </ArticleText>
          </Article>

          <Divider />
          <SubTitle>제9장 기타</SubTitle>
          <Article>
            <ArticleTitle>제1조 (관할법원)</ArticleTitle>
            <ArticleText>
              본 약관은 대한민국 법령을 따르며, 관할은 서울중앙지방법원으로
              합니다.
            </ArticleText>
          </Article>

          <Divider />
          <SubTitle>부칙</SubTitle>
          <Article>
            <ArticleTitle>제1조 (시행일)</ArticleTitle>
            <ArticleText>본 약관은 2025년 11월 1일부터 시행합니다.</ArticleText>
          </Article>
          <Article>
            <ArticleTitle>제2조 (운영사 정보)</ArticleTitle>
            <ArticleText>운영사: 빅브라운 주식회사</ArticleText>
            <ArticleText>대표이사: 김용환</ArticleText>
            <ArticleText>주소: 서울특별시 테헤란로 437, 10F</ArticleText>
            <ArticleText>사업자등록번호: 606-87-02388</ArticleText>
            <ArticleText>
              통신판매업 신고번호: 제2025-서울강남-06035호
            </ArticleText>
          </Article>
          <Article>
            <ArticleTitle>제3조 (개인정보 보호책임자)</ArticleTitle>
            <ArticleText>성명: 홍준호</ArticleText>
            <ArticleText>소속: 플랫폼비즈니스사업부</ArticleText>
            <ArticleText>직위: 차장</ArticleText>
            <ArticleText>이메일: hello@lalafan.io</ArticleText>
          </Article>
        </ContentWrapper>
      </MainContent>
    </PageContainer>
  );
};

export default TermsService;
